import { useCallback, useEffect, useMemo, useState } from "react";
import { X, ShieldCheck, FileText, Filter } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fmtDateTime, formatIDR } from "@/lib/constants";

/**
 * PROMPT 16-PETTY-CASH-AUDIT-TRAIL-VIEWER (2026-08-26)
 *
 * Forensik dedicated untuk seluruh state change Petty Cash — pengajuan (create),
 * approve, reject, reopen, assign_approver/reassign, verify_actual, transfer,
 * soft_delete, restore, hard_delete, purge, edit. Menampilkan sebagai timeline
 * urut waktu (desc) dengan filter petty_cash_id / actor / action / week_label /
 * date-range / free-search. Setiap entry expandable ke JSON detail (old_value,
 * new_value, approver_before/after, delete_reason, dst).
 *
 * Backend: GET /api/petty-cash/audit-trail (gated by petty_cash.view).
 */
const PAGE_SIZES = [25, 50, 100];

const ACTION_META = {
  approve:            { label: "Disetujui",                 color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  reject:             { label: "Ditolak",                   color: "bg-rose-100 text-rose-800 border-rose-300" },
  reopen:             { label: "Dibuka Ulang",              color: "bg-violet-100 text-violet-800 border-violet-300" },
  assign_approver:    { label: "Assign Approver",           color: "bg-orange-100 text-orange-800 border-orange-300" },
  reassign_approver:  { label: "Re-assign Approver",        color: "bg-orange-100 text-orange-800 border-orange-300" },
  unassign_approver:  { label: "Unassign Approver",         color: "bg-slate-100 text-slate-700 border-slate-300" },
  verify:             { label: "Verifikasi 1 (Realisasi)",  color: "bg-sky-100 text-sky-800 border-sky-300" },
  verify_actual:      { label: "Verifikasi 1 (Aktual)",     color: "bg-sky-100 text-sky-800 border-sky-300" },
  v1_verify_item:     { label: "Verifikasi 1 — Item Disahkan", color: "bg-sky-100 text-sky-800 border-sky-300" },
  v1_reject_item:     { label: "Verifikasi 1 — Item Ditolak", color: "bg-rose-100 text-rose-800 border-rose-300" },
  v1_request_revision_item: { label: "Verifikasi 1 — Minta Perbaikan", color: "bg-amber-100 text-amber-800 border-amber-300" },
  v1_revision_reset:  { label: "Verifikasi 1 — Dievaluasi Ulang", color: "bg-amber-100 text-amber-800 border-amber-300" },
  v1_auto_close:      { label: "Verifikasi 1 — Selesai Otomatis (Item Dibatalkan)", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  change_item_coa:    { label: "Verifikasi 2 — Penetapan COA", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  coa_finding:        { label: "Verifikasi 2 — Temuan Pemeriksa COA", color: "bg-amber-100 text-amber-800 border-amber-300" },
  transfer:           { label: "Konfirmasi Transfer",       color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  edit:               { label: "Edit Pengajuan",            color: "bg-amber-100 text-amber-800 border-amber-300" },
  soft_delete:        { label: "Hapus (Soft)",              color: "bg-rose-100 text-rose-800 border-rose-300" },
  hard_delete:        { label: "Hapus Permanen",            color: "bg-rose-200 text-rose-900 border-rose-400" },
  restore:            { label: "Dipulihkan",                color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  purge:              { label: "Hapus Arsip Lama",          color: "bg-slate-200 text-slate-800 border-slate-400" },
  create:             { label: "Dibuat",                    color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const actionMeta = (a) => ACTION_META[a] || { label: a || "—", color: "bg-slate-100 text-slate-700 border-slate-300" };

function ActorBadge({ name, division, level }) {
  if (!name) return <span className="text-slate-400 italic">—</span>;
  return (
    <div>
      <div className="font-semibold text-[#0B1D3A] text-[11px]">{name}</div>
      <div className="text-[9px] text-slate-500">{division || "—"} · Lv{level || "—"}</div>
    </div>
  );
}

function TrailRow({ row, expanded, onToggle }) {
  const meta = actionMeta(row.action);
  const hasDetails = row.old_value || row.new_value || row.approver_before || row.approver_after || row.note;
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pc-audit-row-${row.id}`}>
        <td className="px-3 py-2 tabular-nums text-slate-600 whitespace-nowrap text-[10px] align-top">
          {(row.created_at || "").slice(0, 19).replace("T", " ")}
        </td>
        <td className="px-3 py-2 align-top">
          <ActorBadge name={row.actor_name} division={row.actor_division} level={row.actor_level} />
        </td>
        <td className="px-3 py-2 align-top">
          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${meta.color}`} data-testid={`pc-audit-action-${row.id}`}>
            {meta.label}
          </span>
        </td>
        <td className="px-3 py-2 align-top">
          <div className="font-semibold text-slate-800 text-[11px]">{row.week_label || "—"}</div>
          <div className="text-[10px] text-slate-500 truncate max-w-[180px]" title={row.project_name}>{row.project_name || "—"}</div>
          {row.submitter_name && (
            <div className="text-[9px] text-slate-400">by {row.submitter_name}</div>
          )}
        </td>
        <td className="px-3 py-2 align-top text-[10px] text-slate-600">
          {row.approver_before && row.approver_after && (row.approver_before.id !== row.approver_after.id) && (
            <div>
              <span className="text-slate-500">Approver:</span>{" "}
              <span className="line-through text-slate-400">{row.approver_before.name || "—"}</span>
              {" → "}
              <span className="font-semibold text-slate-800">{row.approver_after.name || "—"}</span>
            </div>
          )}
          {row.old_value?.status && row.new_value?.status && row.old_value.status !== row.new_value.status && (
            <div>
              <span className="text-slate-500">Status:</span>{" "}
              <span className="line-through text-slate-400">{row.old_value.status}</span>
              {" → "}
              <span className="font-semibold text-slate-800">{row.new_value.status}</span>
            </div>
          )}
          {row.note && <div className="italic text-slate-700 mt-1 max-w-[240px]">&ldquo;{row.note}&rdquo;</div>}
        </td>
        <td className="px-3 py-2 text-right align-top">
          {hasDetails && (
            <button
              type="button"
              onClick={onToggle}
              data-testid={`pc-audit-detail-${row.id}`}
              className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline"
            >{expanded ? "Tutup" : "Detail"}</button>
          )}
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr className="bg-slate-50 border-b border-slate-200">
          <td colSpan={6} className="px-3 py-2">
            <pre className="text-[10px] text-slate-700 whitespace-pre-wrap break-all font-mono max-h-72 overflow-auto" data-testid={`pc-audit-detail-body-${row.id}`}>
              {JSON.stringify({
                petty_cash_id: row.petty_cash_id,
                old_value: row.old_value,
                new_value: row.new_value,
                approver_before: row.approver_before,
                approver_after: row.approver_after,
                note: row.note,
              }, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PettyCashAuditTrailViewer({ open, onClose, initialPettyCashId = null }) {
  const [pettyCashId, setPettyCashId] = useState(initialPettyCashId || "");
  const [action, setAction] = useState("");
  const [weekLabel, setWeekLabel] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0, actions: [] });
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (open) setPettyCashId(initialPettyCashId || "");
  }, [open, initialPettyCashId]);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (pettyCashId.trim()) params.petty_cash_id = pettyCashId.trim();
      if (action) params.action = action;
      if (weekLabel.trim()) params.week_label = weekLabel.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (q.trim()) params.q = q.trim();
      const { data: d } = await api.get("/petty-cash/audit-trail", { params });
      setData(d || { items: [], total: 0, actions: [] });
    } catch {
      setData({ items: [], total: 0, actions: [] });
    }
    setLoading(false);
  }, [open, page, pageSize, pettyCashId, action, weekLabel, fromDate, toDate, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [pettyCashId, action, weekLabel, fromDate, toDate, q, pageSize]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  const summary = useMemo(() => {
    const counts = {};
    for (const r of data.items || []) {
      counts[r.action] = (counts[r.action] || 0) + 1;
    }
    return counts;
  }, [data.items]);

  const resetFilters = () => {
    setPettyCashId("");
    setAction("");
    setWeekLabel("");
    setFromDate("");
    setToDate("");
    setQ("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent
        className="max-w-[95vw] w-[1400px] max-h-[92vh] p-0 rounded-none border border-slate-300 flex flex-col"
        data-testid="pc-audit-trail-dialog"
      >
        <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-violet-50 via-white to-slate-50 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-violet-700" />
            <div>
              <div className="font-bold text-[#0B1D3A] uppercase tracking-wider text-sm flex items-center gap-2">
                Petty Cash — Audit Trail Viewer
                {initialPettyCashId && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-violet-100 text-violet-800 border border-violet-300" title={initialPettyCashId}>
                    Trail single pengajuan
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-500">Forensik lengkap setiap tindakan Petty Cash — actor, waktu, before/after, approver_before/after, reason. Backend-source of truth.</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-none"
            data-testid="pc-audit-close"
          ><X size={18} /></button>
        </div>

        <div className="p-3 border-b border-slate-200 bg-slate-50 grid grid-cols-1 md:grid-cols-6 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Filter size={9} /> Petty Cash ID</div>
            <Input
              value={pettyCashId}
              onChange={(e) => setPettyCashId(e.target.value)}
              placeholder="UUID pengajuan"
              className="rounded-none h-8 text-[11px] font-mono"
              data-testid="pc-audit-filter-pcid"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Aksi</div>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full h-8 border border-slate-300 bg-white px-2 text-[11px] rounded-none"
              data-testid="pc-audit-filter-action"
            >
              <option value="">Semua aksi</option>
              {(data.actions || []).map((a) => (
                <option key={a} value={a}>{actionMeta(a).label}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Week Label</div>
            <Input
              value={weekLabel}
              onChange={(e) => setWeekLabel(e.target.value)}
              placeholder="2026-W035"
              className="rounded-none h-8 text-[11px]"
              data-testid="pc-audit-filter-week"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Dari tanggal</div>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-none h-8 text-[11px]" data-testid="pc-audit-filter-from" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sampai tanggal</div>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-none h-8 text-[11px]" data-testid="pc-audit-filter-to" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cari bebas</div>
            <div className="flex gap-1">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="actor / project / note…" onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-none h-8 text-[11px]" data-testid="pc-audit-filter-q" />
              <button
                type="button"
                onClick={resetFilters}
                className="text-[10px] uppercase tracking-wider font-semibold text-white bg-slate-600 hover:bg-slate-700 px-2"
                title="Reset semua filter"
                data-testid="pc-audit-reset"
              >Reset</button>
            </div>
          </div>
        </div>

        {/* Summary badges */}
        {!loading && data.items.length > 0 && (
          <div className="px-3 py-1.5 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap text-[10px]" data-testid="pc-audit-summary">
            <span className="text-slate-500 uppercase tracking-wider font-semibold">Ringkasan halaman:</span>
            {Object.entries(summary).map(([act, n]) => (
              <span key={act} className={`px-1.5 py-0.5 border font-semibold uppercase tracking-wider ${actionMeta(act).color}`}>
                {actionMeta(act).label}: {n}
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs" data-testid="pc-audit-table">
            <thead className="bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Waktu</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Actor</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Aksi</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Pengajuan</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Perubahan</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider text-[10px] w-20">Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 italic">Memuat audit trail…</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 italic">
                  <FileText size={24} className="mx-auto mb-2 opacity-40" />
                  Tidak ada entry audit yang cocok. Coba kurangi filter atau lebarkan rentang tanggal.
                </td></tr>
              )}
              {!loading && data.items.map((row) => (
                <TrailRow
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Total: <span className="font-semibold text-[#0B1D3A]" data-testid="pc-audit-total">{data.total}</span> entry
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-7 border border-slate-300 bg-white px-1.5 text-[10px] rounded-none"
              data-testid="pc-audit-page-size"
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / halaman</option>)}
            </select>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-800 border border-slate-300 px-2 py-1 disabled:opacity-30"
              data-testid="pc-audit-prev"
            >◀ Prev</button>
            <span className="text-[10px] text-slate-600" data-testid="pc-audit-page-info">
              Halaman {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-800 border border-slate-300 px-2 py-1 disabled:opacity-30"
              data-testid="pc-audit-next"
            >Next ▶</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
